import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, SearchField, Table } from '@factorialco/deprecated-design-system'
import type { TeamQuery } from 'generated/resources/graphql'
import _ from 'lodash'

import { ExplotableList } from 'modules/core/components/GroupingExplotable'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type { PolicyTarget } from 'modules/permissions/store/collections/policyAuthorizations'
// eslint-disable-next-line no-restricted-imports -- Required for runtime policy authorization checks
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'
import TeamsMembersModal from 'modules/teams/components/MembersModal'
import type { ITeam } from 'modules/teams/store/models/Team'

import BlankState from './BlankState'
import ExpandableGroup from './ExpandableGroup'
import TableHeader from './_Header'
import Item from './_Item'

type TeamMembership = NonNullable<NonNullable<TeamQuery['teams']['team']>['memberships']>[number]

interface Props {
  team: ITeam
  readonly?: boolean
  memberships: TeamMembership[]
  hasDevJobCatalogTree: boolean
}

type MembershipsPerRole = Record<number | string, TeamMembership[]>

const getEmployeeFullName = (employee: TeamMembership['employee']) => {
  return `${employee.firstName} ${employee.lastName}`
}

const getEmployeeRole = (employee: TeamMembership['employee']) => {
  return employee.referenceContract?.jobCatalogLevel?.role
}

const employeeToPolicyTarget = (employeeId: number): PolicyTarget => {
  return {
    id: employeeId,
    policyTargetClass: '::Employee',
  }
}

const TeamList = ({ team, readonly, memberships, hasDevJobCatalogTree }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = React.useState(false)

  // Filter to only non-lead, non-terminated members
  const members = React.useMemo(
    () => memberships.filter((m) => !m.lead && !m.employee.terminatedOn),
    [memberships]
  )

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  // Sort and filter members
  const sortedAndFilteredMembers = React.useMemo(() => {
    const filtered = members.filter((m) =>
      matchText(debouncedSearch, getEmployeeFullName(m.employee))
    )
    return _.orderBy(filtered, [(m) => m.employee.firstName], ['asc'])
  }, [members, debouncedSearch])

  // Group members by job role
  const membershipsPerJobRole = React.useMemo(() => {
    return sortedAndFilteredMembers.reduce<MembershipsPerRole>((acc, membership) => {
      const role = getEmployeeRole(membership.employee)
      const key = role ? role.id : 'undefined'
      const current = acc[key] || []
      return {
        ...acc,
        [key]: [...current, membership],
      }
    }, {})
  }, [sortedAndFilteredMembers])

  const roles = React.useMemo(() => {
    return _.uniqBy(
      sortedAndFilteredMembers.map((m) => getEmployeeRole(m.employee)),
      (role) => role?.id ?? 'undefined'
    )
  }, [sortedAndFilteredMembers])

  const unassignedRoleMembers = React.useMemo(() => {
    return membershipsPerJobRole['undefined'] || []
  }, [membershipsPerJobRole])

  const unassignedRoleEmployeesIds = React.useMemo(() => {
    return unassignedRoleMembers
      .filter((m) =>
        policyAuthorizations.for('contracts.assign_jobs', employeeToPolicyTarget(m.employee.id))
      )
      .map((m) => m.employee.id)
  }, [unassignedRoleMembers])

  useAsyncData(
    () =>
      Promise.all([
        policyAuthorizations.authorizePolicies('job_catalog.create_job'),
        policyAuthorizations.authorizePolicies(
          'contracts.assign_jobs',
          unassignedRoleMembers.map((m) => employeeToPolicyTarget(m.employee.id))
        ),
      ]),
    []
  )

  const canCreateJob = policyAuthorizations.for('job_catalog.create_job')

  const tableOptions = () => (
    <Box width="full">
      <SearchField
        variant="big"
        search={search}
        onSearch={onSearch}
        placeholder={i18n.t('common.search')}
      />
    </Box>
  )

  const handleBulkEdit = () => {
    navigate(
      `${navigation.employees.jobCatalog.bulkEdit.fullPath()}?employeesIds=[${unassignedRoleEmployeesIds}]&state=unnasigned`
    )
    track(trackingEvents.teams.createJobThroughTeamList)
  }

  const rolesGroups = roles.map((role) => {
    const id = role?.id
    const key = id ?? 'undefined'
    const roleMemberships = membershipsPerJobRole[key] ?? []

    if (!sortedAndFilteredMembers.length) {
      return null
    }

    if (hasDevJobCatalogTree) {
      return (
        <Table noRadius hideBorder key={id}>
          <Table.Body>
            {roleMemberships.map((membership) => {
              return (
                <Item
                  key={membership.employee.id}
                  membership={membership}
                  team={team}
                  readonly={readonly}
                  hasDevJobCatalogTree={hasDevJobCatalogTree}
                />
              )
            })}
          </Table.Body>
        </Table>
      )
    }

    return (
      <ExpandableGroup
        key={id}
        id={id}
        count={roleMemberships.length}
        name={role?.name ?? i18n.t('teams.unassigned_role')}
        onBulkEdit={handleBulkEdit}
        canAssingJobs={!!unassignedRoleEmployeesIds?.length && canCreateJob}
        teamId={team.id}
      >
        {roleMemberships.map((membership) => {
          return (
            <Item
              key={membership.employee.id}
              membership={membership}
              team={team}
              readonly={readonly}
            />
          )
        })}
      </ExpandableGroup>
    )
  })

  return (
    <>
      <Table options={tableOptions} overflowY="auto" noBottomRadius fullWidth>
        <Table noRadius hideBorder>
          <TableHeader hasDevJobCatalogTree={hasDevJobCatalogTree} />
        </Table>
        <Table noRadius hideBorder>
          <BlankState
            show={!sortedAndFilteredMembers.length}
            readonly={readonly}
            onAddMembers={() => setModalOpen(true)}
          />
          <ExplotableList>{rolesGroups}</ExplotableList>
        </Table>
      </Table>
      {modalOpen && (
        <TeamsMembersModal
          selectedGroup="members"
          onClose={() => setModalOpen(false)}
          team={team}
        />
      )}
    </>
  )
}

export default TeamList
